import { useState } from "react";
import { Link } from "react-router-dom";
import StarRating from "@/components/base/StarRating";

export interface ReviewCardData {
  id: string;
  episodeId: string;
  author: string;
  avatarSeed: string;
  rating: number;
  date: string;
  content: string;
  likes: number;
}

interface ReviewCardProps {
  review: ReviewCardData;
  showEpisode?: boolean;
  episodeLabel?: string;
}

export default function ReviewCard({
  review,
  showEpisode = false,
  episodeLabel,
}: ReviewCardProps) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(review.likes);

  const toggleLike = () => {
    setLiked((prev) => !prev);
    setLikes((count) => (liked ? count - 1 : count + 1));
  };

  return (
    <article className="p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 flex flex-col h-full">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-600 text-background-50 font-heading font-bold text-sm">
          {review.avatarSeed}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground-950 truncate">{review.author}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <StarRating rating={review.rating} size="sm" />
            <span className="text-xs text-foreground-500">{review.date}</span>
          </div>
        </div>
      </div>

      <p className="mt-3 text-sm text-foreground-700 leading-relaxed flex-1">
        {review.content}
      </p>

      <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between gap-3">
        {showEpisode && episodeLabel ? (
          <Link
            to={`/detail/${review.episodeId}`}
            className="text-xs text-accent-400 hover:text-accent-300 flex items-center gap-1 cursor-pointer transition-colors"
          >
            <i className="ri-link" />
            {episodeLabel}
          </Link>
        ) : (
          <span className="text-xs text-foreground-500">观众点评</span>
        )}
        <button
          type="button"
          onClick={toggleLike}
          aria-pressed={liked}
          aria-label="点赞这条点评"
          className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-xs whitespace-nowrap cursor-pointer transition-colors ${
            liked
              ? "bg-primary-600 text-background-50"
              : "bg-background-200 text-foreground-700 hover:text-accent-300"
          }`}
        >
          <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
          {likes}
        </button>
      </div>
    </article>
  );
}